import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Factory, ArrowRight, UploadCloud, GitCompareArrows, PackageCheck, MessagesSquare, Cpu, Truck, MapPin, Phone, Mail } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/8865187/pexels-photo-8865187.jpeg";
const PROC_IMG = "https://images.unsplash.com/photo-1723632670536-e3104577a19c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxtZXRhbCUyMHBhcnRzJTIwZW5naW5lZXJpbmclMjBwcm90b3R5cGV8ZW58MHx8fHwxNzgyODkxMzA3fDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: UploadCloud, t: "Upload CAD", d: "Drop STEP, STL or drawings and define specs, materials, tolerances." },
  { icon: Cpu, t: "Instant estimate", d: "Get a rule-based price the moment you submit — plus AI RFQ analysis." },
  { icon: GitCompareArrows, t: "Compare quotes", d: "Vetted suppliers bid. Compare price, lead time and rating side by side." },
  { icon: PackageCheck, t: "Order & track", d: "Convert a quote to an order and follow every production stage." },
  { icon: MessagesSquare, t: "Collaborate", d: "Message suppliers directly against each RFQ with full history." },
  { icon: Truck, t: "Delivery", d: "Live shipment tracking with carrier, tracking number and ETA." },
];

export default function Landing() {
  const { user } = useAuth();
  const dash = user ? (user.role === "buyer" ? "/buyer" : user.role === "supplier" ? "/supplier" : "/admin") : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="h-16 border-b border-border sticky top-0 bg-background/90 backdrop-blur z-30">
        <div className="max-w-7xl mx-auto h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 bg-primary grid place-items-center rounded-sm"><Factory className="h-4 w-4 text-primary-foreground" /></div>
            <span className="font-heading font-extrabold text-lg tracking-tight">Nepton</span>
          </div>
          <nav className="flex items-center gap-3">
            <a href="#contact" data-testid="nav-contact" className="hidden sm:block px-4 py-2 text-sm hover:text-brand transition-colors">Contact</a>
            {dash ? (
              <Link to={dash} data-testid="nav-dashboard" className="px-4 py-2 bg-primary text-primary-foreground text-sm rounded-sm hover:bg-brand transition-colors">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-sm hover:text-brand transition-colors">Sign in</Link>
                <Link to="/register" data-testid="nav-register" className="px-4 py-2 bg-primary text-primary-foreground text-sm rounded-sm hover:bg-brand transition-colors">Get started</Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6 py-20 lg:py-28">
            <div className="label-tech text-brand mb-5">On-demand manufacturing marketplace</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02]">
              Source custom parts.<br /><span className="text-brand">From prototype to production.</span>
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground max-w-lg">
              Upload your CAD files, request quotes from vetted suppliers, compare pricing and lead times, then place and track your order — all in one industrial-grade workspace.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-buyer" className="group inline-flex items-center gap-2 px-6 py-3 bg-brand text-white rounded-sm hover:bg-brand-dark transition-colors">
                Request a quote <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/register" data-testid="hero-cta-supplier" className="inline-flex items-center gap-2 px-6 py-3 border border-primary rounded-sm hover:bg-accent transition-colors">
                Join as a supplier
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[["7 days", "Avg. lead time"], ["100+", "Materials & processes"], ["24/7", "Order tracking"]].map(([a, b]) => (
                <div key={b}>
                  <div className="font-heading text-2xl font-bold">{a}</div>
                  <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">{b}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 relative h-64 lg:h-[560px]">
            <img src={HERO_IMG} alt="CNC machining" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="label-tech text-muted-foreground mb-3">How it works</div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">A complete quote-to-delivery workflow, built for engineers.</h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 border border-border rounded-sm overflow-hidden">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.t} className="p-8 border-b border-r border-border last:border-r-0 hover:bg-secondary/50 transition-colors">
                <div className="flex items-center justify-between">
                  <Icon className="h-6 w-6 text-brand" />
                  <span className="font-mono text-xs text-muted-foreground">0{i + 1}</span>
                </div>
                <h3 className="mt-5 text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA band */}
      <section className="relative border-y border-border">
        <img src={PROC_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-2xl mx-auto">Ready to move your next part into production?</h2>
          <Link to="/register" data-testid="cta-band-register" className="mt-8 inline-flex items-center gap-2 px-7 py-3 bg-brand text-white rounded-sm hover:bg-brand-dark transition-colors">
            Create your free account <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-tech text-brand mb-3">Contact us</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Let's build your next part.</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground max-w-md">
              Questions about materials, tolerances or bulk production? Reach out to the Nepton team — we're here to help you go from CAD to delivered parts.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 border-t border-l border-border rounded-sm overflow-hidden">
            <div className="sm:col-span-2 p-6 border-r border-b border-border">
              <div className="label-tech text-muted-foreground">Company</div>
              <div className="mt-1.5 font-heading text-lg font-semibold">Nepton</div>
              <div className="text-sm text-muted-foreground">On-demand manufacturing marketplace</div>
            </div>
            <div className="p-6 border-r border-b border-border" data-testid="contact-address">
              <MapPin className="h-5 w-5 text-brand" />
              <div className="label-tech text-muted-foreground mt-3">Address</div>
              <div className="mt-1 text-sm leading-relaxed">Dhayari, Pune,<br />Maharashtra, India — 411041</div>
            </div>
            <div className="p-6 border-r border-b border-border">
              <div className="flex flex-col gap-6">
                <div data-testid="contact-phone">
                  <Phone className="h-5 w-5 text-brand" />
                  <div className="label-tech text-muted-foreground mt-3">Cell</div>
                  <a href="tel:+917773949601" className="mt-1 block text-sm font-mono hover:text-brand transition-colors">+91 77739 49601</a>
                </div>
                <div data-testid="contact-email">
                  <Mail className="h-5 w-5 text-brand" />
                  <div className="label-tech text-muted-foreground mt-3">Email</div>
                  <a href="mailto:Info@neptonhq.com" className="mt-1 block text-sm font-mono hover:text-brand transition-colors">Info@neptonhq.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2"><Factory className="h-4 w-4" /> Nepton</div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-xs">
          <a href="tel:+917773949601" className="hover:text-brand transition-colors">+91 77739 49601</a>
          <a href="mailto:Info@neptonhq.com" className="hover:text-brand transition-colors">Info@neptonhq.com</a>
          <span>Dhayari, Pune, Maharashtra 411041</span>
        </div>
        <div className="font-mono text-xs">© 2026 · Manufacturing Marketplace</div>
      </footer>
    </div>
  );
}
